// MN-2071 Phase 2 (ViewerLocal): client-side rendering for a class occasion's schedule.
//
// The server always builds a `dates` string in the class's OWN time zone
// (e.g. "8/15/2026 3:00 PM - 5:00 PM ET") for both ClassTime and ViewerLocal classes -- it has
// no browser, so it cannot localize. That string is the safe fallback everywhere.
//
// For a timed class the server also sends the derived UTC instants (`startUtc` / `endUtc`,
// ISO-8601 "…Z"). Only the browser knows the viewer's own zone, so we convert those instants
// here and label them with the viewer's zone abbreviation. This lets any viewer flip between the
// class's own time and their own local time (the <EventDates/> toggle), without changing what
// anyone else sees.
//
// Nothing here throws: any missing / invalid data degrades to the server `dates` string.
import React, { useState } from "react";
import moment from "moment-timezone";

// Mirrors Natca.Core.Enums.ClassTimeDisplayPolicy.
export const CLASS_TIME_DISPLAY_POLICY = {
  DateOnly: 1,
  ClassTime: 2,
  ViewerLocal: 3,
};

// Build the "viewer's local zone" string from the derived UTC instants, or null when it cannot
// be produced (date-only class, missing/invalid instants, or a zone-detection failure).
function viewerLocalString(row) {
  if (!row || !row.startUtc || !row.endUtc) {
    return null;
  }

  const showsTime =
    row.displayPolicy === CLASS_TIME_DISPLAY_POLICY.ClassTime ||
    row.displayPolicy === CLASS_TIME_DISPLAY_POLICY.ViewerLocal;
  if (!showsTime) {
    return null;
  }

  try {
    const zone = moment.tz.guess();
    const start = moment.utc(row.startUtc).tz(zone);
    const end = moment.utc(row.endUtc).tz(zone);
    if (!start.isValid() || !end.isValid()) {
      return null;
    }

    // moment-timezone's "z" token yields the zone abbreviation (e.g. PKT, EDT, PST); zones with
    // no named abbreviation fall back to a numeric offset like "+05" -- still a single token.
    const abbr = start.format("z");

    if (start.isSame(end, "day")) {
      return (
        start.format("M/D/YYYY h:mm A") + " - " + end.format("h:mm A") + " " + abbr
      );
    }
    return (
      start.format("M/D/YYYY h:mm A") +
      " - " +
      end.format("M/D/YYYY h:mm A") +
      " " +
      abbr
    );
  } catch (e) {
    // Any conversion failure falls back to the server string (no toggle offered).
    return null;
  }
}

// Drop the trailing zone token (" ET", " PKT", " +05") so two schedule strings can be compared
// on wall-clock alone. Server labels ("ET", "CT", …) and moment abbreviations are all single
// tokens, so this reliably isolates the date+time portion.
function stripZoneLabel(value) {
  return (value || "").replace(/\s+\S+\s*$/, "").trim();
}

// Everything the display needs: the class-zone string (server), the viewer-zone string (client),
// which one is the default, and whether a toggle between them is actually meaningful.
export function computeEventViews(row) {
  const classZone = (row && row.dates) || "";
  const viewerZone = viewerLocalString(row);
  const isViewerPolicy =
    !!row && row.displayPolicy === CLASS_TIME_DISPLAY_POLICY.ViewerLocal;

  // Only offer a toggle when the alternate view exists AND actually differs. A viewer already in
  // the class's zone would otherwise see the same wall-clock twice with only the label changing
  // (e.g. "3:00 PM ET" vs "3:00 PM EDT") -- a pointless, confusing flip.
  const canToggle =
    !!viewerZone && stripZoneLabel(viewerZone) !== stripZoneLabel(classZone);

  // ViewerLocal classes default to the viewer's zone; everything else defaults to class zone.
  const defaultView = isViewerPolicy && viewerZone ? "viewer" : "class";

  return { classZone, viewerZone, defaultView, canToggle };
}

// Backward-compatible plain-string formatter. Used where a string (not a component) is required
// -- e.g. the export filename derived from the "registrationWindow" prop. Returns the default
// view's text.
export default function formatEventDates(row) {
  if (!row) {
    return "";
  }
  const { classZone, viewerZone, defaultView } = computeEventViews(row);
  return defaultView === "viewer" && viewerZone ? viewerZone : classZone;
}

// Interactive display of a class schedule. Shows the default view and, when a different local
// time is available, a small link to flip between the class's own zone and the viewer's local
// zone. Each instance keeps its own toggle state, so per-row toggling in a list is independent.
export function EventDates({ row }) {
  const { classZone, viewerZone, defaultView, canToggle } = computeEventViews(row);
  const [showViewer, setShowViewer] = useState(defaultView === "viewer");

  if (!row) {
    return null;
  }

  // Nothing meaningful to toggle to: render the best single string with no link.
  if (!canToggle) {
    return <React.Fragment>{showViewer && viewerZone ? viewerZone : classZone}</React.Fragment>;
  }

  const current = showViewer ? viewerZone : classZone;
  const linkText = showViewer ? "show class time" : "show my time";
  const title = showViewer
    ? "Showing your local time. Click to show the class's own time zone."
    : "Showing the class's own time zone. Click to show your local time.";

  return (
    <React.Fragment>
      {current}{" "}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 align-baseline"
        style={{ fontSize: "0.85em", verticalAlign: "baseline" }}
        title={title}
        onClick={(e) => {
          // Don't let the click bubble to a surrounding row/card click handler.
          e.preventDefault();
          e.stopPropagation();
          setShowViewer((prev) => !prev);
        }}
      >
        ({linkText})
      </button>
    </React.Fragment>
  );
}
